import type { FilterRuleModel, ValueFilterRule } from '@/shared/generated'
import type { SingleParser } from 'nuqs'

import { useCallback, useMemo } from 'react'
import { parseAsArrayOf, parseAsString, useQueryStates } from 'nuqs'
import { useTranslation } from 'react-i18next'

import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import { getUsername, useAllUsersQuery } from '@/entities/user'

import { Operators } from '@/shared/components/query-builder'
import { SelectFilter } from '@/shared/components/refinement-bar'
import { DateRangeFilter } from '@/shared/components/refinement-bar/fields'

const queryStateOptions = {
	scroll: false,
	shallow: true,
	throttleMs: 50,
	debounceMs: 300,
	clearOnDefault: false,
}
export const useWhatifRunsQueryFilters = () => {
	const { t } = useTranslation()

	const optimizationGroupsQuery = useOptimizationGroupsQuery()
	const usersQuery = useAllUsersQuery()
	const dataChoices = useMemo(
		() => ({
			users:
				usersQuery.data?.map((user) => ({
					label: getUsername(user),
					value: user.id,
				})) || [],
			optimizationGroups:
				optimizationGroupsQuery.data?.map((optimizationGroup) => ({
					label: optimizationGroup.name,
					value: optimizationGroup.id,
				})) || [],
		}),
		[usersQuery.data, optimizationGroupsQuery.data]
	)
	const fieldsConfig = useMemo(
		() => ({
			created_at: {
				label: t('created_at', { defaultValue: 'Created at' }),
				type: DateRangeFilter,
				getInitialValue: () => ({
					operation: Operators.DATE_BETWEEN,
					value: [],
				}),
			},
			created_by: {
				label: t('created_by', { defaultValue: 'Created by' }),
				type: SelectFilter,
				getInitialValue: () => ({
					operation: Operators.IN,
					value: [],
				}),
				useSelectOptions: () => ({
					data: dataChoices.users,
				}),
			},
			optimization_group_ids: {
				label: t('optimization_groups', {
					defaultValue: 'Optimization groups',
				}),
				type: SelectFilter,
				getInitialValue: () => ({
					operation: Operators.ANY_OF,
					value: [],
				}),
				useSelectOptions: () => ({
					data: dataChoices.optimizationGroups,
				}),
			},
		}),
		[dataChoices]
	)
	const filterParsers = useMemo(
		() =>
			Object.entries(fieldsConfig).reduce<
				Record<string, SingleParser<string> | SingleParser<string[]>>
			>((acc, [id, conf]) => {
				if ([SelectFilter, DateRangeFilter].includes(conf.type)) {
					acc[id] = parseAsArrayOf(parseAsString, ',').withOptions(
						queryStateOptions
					)
				} else {
					acc[id] = parseAsString.withOptions(queryStateOptions)
				}

				return acc
			}, {}),
		[fieldsConfig]
	)
	const [filterValues, setFilterValues] = useQueryStates(filterParsers)
	const filters = useMemo(
		() =>
			Object.entries(filterValues).reduce((filters, [key, value]) => {
				if (value !== null) {
					const processedValue = Array.isArray(value)
						? value
						: typeof value === 'string' && /[^a-zA-Z0-9]/.test(value)
							? value.split(/[^a-zA-Z0-9]+/).filter(Boolean)
							: [value]

					filters.push({
						name: key,
						operation: fieldsConfig[
							key as keyof typeof fieldsConfig
						].getInitialValue()?.operation as any,
						value: processedValue,
					})
				}

				return filters
			}, [] as FilterRuleModel[]),
		[filterValues]
	)
	const setFilters = useCallback(
		(next: ValueFilterRule[]) => {
			const filterUpdates = next.reduce<
				Record<string, string | string[] | null>
			>((acc, filter) => {
				acc[filter.name] = filter.value as string | string[]

				return acc
			}, {})

			for (const prevFilter of filters) {
				if (!next.some((filter) => filter.name === prevFilter.name)) {
					filterUpdates[prevFilter.name] = null
				}
			}
			setFilterValues(filterUpdates)

			return next
		},
		[filters, setFilterValues]
	)

	return useMemo(
		() => ({
			dataChoices,
			fieldsConfig,
			filters,
			setFilters,
		}),
		[dataChoices, fieldsConfig, filters, setFilters]
	)
}
